import { useEffect, useMemo, useState } from "react";
import { Search, SlidersHorizontal } from "lucide-react";
import api from "@/lib/api";
import CarCard from "@/components/site/CarCard";
import CarDetailDialog from "@/components/site/CarDetailDialog";

const FUEL_OPTIONS = ["All", "Petrol", "Diesel", "Hybrid", "Electric", "LPG"];
const TRANS_OPTIONS = ["All", "Manual", "Automatic"];

export default function StockSection({ settings }) {
    const [cars, setCars] = useState([]);
    const [loading, setLoading] = useState(true);
    const [makes, setMakes] = useState([]);

    const [q, setQ] = useState("");
    const [make, setMake] = useState("All");
    const [model, setModel] = useState("");
    const [fuel, setFuel] = useState("All");
    const [transmission, setTransmission] = useState("All");
    const [yearFrom, setYearFrom] = useState("");
    const [yearTo, setYearTo] = useState("");
    const [selectedCar, setSelectedCar] = useState(null);

    useEffect(() => {
        (async () => {
            const r = await api.get("/cars/makes");
            setMakes(r.data || []);
        })();
    }, []);

    useEffect(() => {
        const t = setTimeout(async () => {
            setLoading(true);
            const params = {};
            if (q) params.q = q;
            if (make && make !== "All") params.make = make;
            if (model) params.model = model;
            if (fuel && fuel !== "All") params.fuel = fuel;
            if (transmission && transmission !== "All") params.transmission = transmission;
            if (yearFrom) params.year_from = yearFrom;
            if (yearTo) params.year_to = yearTo;
            try {
                const r = await api.get("/cars", { params });
                setCars(r.data || []);
            } finally {
                setLoading(false);
            }
        }, 200);
        return () => clearTimeout(t);
    }, [q, make, model, fuel, transmission, yearFrom, yearTo]);

    const count = cars.length;
    const makeOptions = useMemo(() => ["All", ...makes], [makes]);

    const clearFilters = () => {
        setQ("");
        setMake("All");
        setModel("");
        setFuel("All");
        setTransmission("All");
        setYearFrom("");
        setYearTo("");
    };

    return (
        <section
            id="stock"
            data-testid="stock-section"
            className="relative bg-[#0a0a0a] py-20 md:py-28 border-t border-white/10"
        >
            <div className="max-w-7xl mx-auto px-5 md:px-10">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-[#d52b1e] mb-3">
                            Live Inventory
                        </div>
                        <h2 className="font-display uppercase text-4xl sm:text-5xl tracking-tight">
                            Vehicles in stock
                        </h2>
                        <p className="text-zinc-400 mt-3 max-w-2xl">
                            Every car listed below is currently in our yard being dismantled.
                            Click a vehicle to see parts available or inquire directly.
                        </p>
                    </div>
                    <div
                        className="text-right font-display text-5xl text-[#d52b1e]"
                        data-testid="stock-count"
                    >
                        {count.toString().padStart(2, "0")}
                        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 font-sans">
                            Cars Available
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-[#18181a] border border-white/10 p-5 md:p-7 mb-10">
                    <div className="flex items-center gap-2 mb-5">
                        <SlidersHorizontal className="w-4 h-4 text-[#d52b1e]" />
                        <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-300">
                            Search & Filter
                        </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-5">
                        <div className="lg:col-span-2 relative">
                            <Search className="absolute left-0 top-3.5 w-4 h-4 text-zinc-500" />
                            <input
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search make, model or keyword..."
                                className="industrial-input pl-6"
                                data-testid="stock-search-input"
                            />
                        </div>
                        <select
                            value={make}
                            onChange={(e) => setMake(e.target.value)}
                            className="industrial-input"
                            data-testid="stock-make-select"
                        >
                            {makeOptions.map((m) => (
                                <option key={m} value={m} className="bg-[#0a0a0a]">
                                    {m === "All" ? "ALL MAKES" : m}
                                </option>
                            ))}
                        </select>
                        <input
                            value={model}
                            onChange={(e) => setModel(e.target.value)}
                            placeholder="Model"
                            className="industrial-input"
                            data-testid="stock-model-input"
                        />
                        <select
                            value={fuel}
                            onChange={(e) => setFuel(e.target.value)}
                            className="industrial-input"
                            data-testid="stock-fuel-select"
                        >
                            {FUEL_OPTIONS.map((f) => (
                                <option key={f} value={f} className="bg-[#0a0a0a]">
                                    {f === "All" ? "ALL FUEL" : f.toUpperCase()}
                                </option>
                            ))}
                        </select>
                        <select
                            value={transmission}
                            onChange={(e) => setTransmission(e.target.value)}
                            className="industrial-input"
                            data-testid="stock-transmission-select"
                        >
                            {TRANS_OPTIONS.map((t) => (
                                <option key={t} value={t} className="bg-[#0a0a0a]">
                                    {t === "All" ? "ALL TRANS." : t.toUpperCase()}
                                </option>
                            ))}
                        </select>
                        <input
                            type="number"
                            value={yearFrom}
                            onChange={(e) => setYearFrom(e.target.value)}
                            placeholder="Year from"
                            className="industrial-input"
                            data-testid="stock-year-from"
                        />
                        <input
                            type="number"
                            value={yearTo}
                            onChange={(e) => setYearTo(e.target.value)}
                            placeholder="Year to"
                            className="industrial-input"
                            data-testid="stock-year-to"
                        />
                    </div>
                    <div className="mt-5 flex justify-end">
                        <button
                            onClick={clearFilters}
                            className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 hover:text-white transition-colors"
                            data-testid="stock-clear-filters"
                        >
                            Clear filters
                        </button>
                    </div>
                </div>

                {/* Results */}
                {loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[...Array(6)].map((_, i) => (
                            <div
                                key={i}
                                className="bg-[#18181a] border border-white/10 h-[380px] animate-pulse"
                            />
                        ))}
                    </div>
                ) : cars.length === 0 ? (
                    <div
                        className="border border-dashed border-white/15 py-20 text-center"
                        data-testid="stock-empty-state"
                    >
                        <div className="font-display text-3xl uppercase mb-2">
                            No vehicles match
                        </div>
                        <div className="text-zinc-400 text-sm">
                            Try adjusting your filters or contact us about a specific vehicle.
                        </div>
                    </div>
                ) : (
                    <div
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
                        data-testid="stock-grid"
                    >
                        {cars.map((c) => (
                            <CarCard key={c.id} car={c} onOpen={() => setSelectedCar(c)} />
                        ))}
                    </div>
                )}
            </div>

            <CarDetailDialog
                car={selectedCar}
                settings={settings}
                open={!!selectedCar}
                onClose={() => setSelectedCar(null)}
            />
        </section>
    );
}
